import { cleanInput } from '@exodus/basic-utils';
import { formatAssetAmount, formatPrice } from '@exodus/formatting';
import { useAtom, useAtomValue } from '@exodus/headless-ui';
import useDebouncedCallback from '@exodus/react-hooks/common/useDebouncedCallback';
import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { INPUT_MODE } from '../../constants.js';
import useTrackAssetConfigureEvent from '../../hooks/use-track-configure-asset.js';
import { swapModeAtom, fromAmountAtom, toAmountAtom, fiatModeAtom, prefillIdAtom, fromAssetAtom, toAssetAtom, } from '../../../atoms/index.js';
const formatValue = (value, maxSignificant) => formatAssetAmount(value, { useGrouping: false, maxSignificant });
const formatFiat = (value) => formatPrice(value.toDefaultNumber(), { useGrouping: false, format: '%v' });
const FIAT_MAX_DECIMALS = 2;
const AssetInput = ({ Component, className, style, mode = INPUT_MODE.CRYPTO, direction, asset, amount, fiatAmount, setFiatAmount, setAmount, loading, disabled, resetAmount, onFocus, onBlur, debounceMs = 200, }, ref) => {
    const componentRef = React.useRef(null);
    const trackAssetConfigureSwap = useTrackAssetConfigureEvent();
    const fiatMode = useAtomValue(fiatModeAtom);
    const isFiat = mode === INPUT_MODE.FIAT || fiatMode;
    const maxDecimals = isFiat ? FIAT_MAX_DECIMALS : asset.currency.defaultUnit.power;
    const [prefillId, setPrefillId] = useAtom(prefillIdAtom);
    const [isFocused, setIsFocused] = useState(false);
    const [valueWhileFocused, setValueWhileFocused] = useState(amount ? formatValue(amount) : '');
    const _value = isFiat ? fiatAmount : amount;
    const value = _value?.isPositive ? _value : undefined;
    const toAmount = useAtomValue(toAmountAtom);
    const fromAmount = useAtomValue(fromAmountAtom);
    const [swapMode, setSwapMode] = useAtom(swapModeAtom);
    const isExactIn = swapMode === 'ExactIn';
    const enterInputAmount = isExactIn ? fromAmount : toAmount;
    const fromAsset = useAtomValue(fromAssetAtom);
    const toAsset = useAtomValue(toAssetAtom);
    useEffect(() => {
        if (enterInputAmount.isZero) {
            resetAmount();
        }
    }, [enterInputAmount.isZero, resetAmount]);
    useEffect(() => {
        if (isFocused) {
            setValueWhileFocused('');
        }
    }, [isFocused, fromAsset, toAsset]);
    useEffect(() => {
        setValueWhileFocused('');
    }, [asset.name]);
    useEffect(() => {
        if (isFiat) {
            setValueWhileFocused(value ? formatFiat(value) : '');
        }
        else {
            setValueWhileFocused(value ? formatValue(value) : '');
        }
    }, [isFiat, isFocused]);
    useEffect(() => {
        if (prefillId && value && !value.isZero) {
            setValueWhileFocused(isFiat ? formatFiat(value) : formatValue(value));
        }
    }, [prefillId, isFiat, value?.value]);
    const debouncedSet = useDebouncedCallback((value) => {
        if (isFiat) {
            setFiatAmount(value);
        }
        else {
            const decimals = asset.currency.defaultUnit.power;
            const cleanedValue = cleanInput(value, decimals);
            setAmount(asset.currency.defaultUnit(cleanedValue));
        }
    }, debounceMs);
    const handleChangeText = useCallback((text, updateState = true) => {
        const cleaned = cleanInput(text, maxDecimals);
        void trackAssetConfigureSwap();
        if (updateState) {
            setSwapMode(direction === 'from' ? 'ExactIn' : 'ExactOut');
            setPrefillId(null);
            debouncedSet(cleaned);
        }
        if (isFocused) {
            setValueWhileFocused(cleaned);
        }
    }, [
        debouncedSet,
        direction,
        isFocused,
        maxDecimals,
        setPrefillId,
        setSwapMode,
        trackAssetConfigureSwap,
    ]);
    const handleBlur = () => {
        onBlur?.();
        setIsFocused(false);
    };
    const handleFocus = () => {
        onFocus?.();
        setIsFocused(true);
    };
    const inputValue = isFocused
        ? cleanInput(valueWhileFocused, maxDecimals)
        : value?.isPositive
            ? isFiat
                ? formatFiat(value)
                : formatValue(value)
            : '';
    useImperativeHandle(ref, () => {
        return {
            focus: componentRef.current?.focus,
            blur: componentRef.current?.blur,
            setValue: (setter, updateState = true) => handleChangeText(setter(inputValue), updateState),
        };
    }, [handleChangeText, inputValue]);
    if (Component) {
        return (React.createElement(Component, { direction: direction, ref: componentRef, value: inputValue, asset: asset, loading: loading, className: className, style: style, onChangeText: handleChangeText, onFocus: handleFocus, onBlur: handleBlur, fiatMode: isFiat, disabled: disabled }));
    }
    return null;
};
export default React.forwardRef(AssetInput);
